import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SIDEBAR_LOCKED_KEY = 'robitest_sidebar_locked';
const HOVER_INTENT_MS = 140;

/**
 * Sidebar lock + hover expansion.
 * - Unlocked (default): collapsed; expands on hover; collapses on leave.
 * - Locked: stays expanded regardless of hover.
 * Persists lock preference in localStorage for the session/browser.
 */
export function useSidebarState() {
  const [locked, setLocked] = useState(() => {
    try {
      const lockVal = localStorage.getItem(SIDEBAR_LOCKED_KEY);
      if (lockVal !== null) return lockVal === 'true';
      const oldCollapsed = localStorage.getItem('robitest_sidebar_collapsed');
      if (oldCollapsed === 'false') return true;
      return false;
    } catch {
      return false;
    }
  });
  const [hovered, setHovered] = useState(false);
  const [forceCollapsed, setForceCollapsed] = useState(false);
  const [peek, setPeek] = useState(false);

  const expanded = forceCollapsed ? false : locked || hovered || peek;

  const toggleLock = useCallback(() => {
    setLocked((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_LOCKED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const hoverTimer = useRef(null);

  const onMouseEnter = useCallback(() => {
    if (forceCollapsed) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHovered(true), HOVER_INTENT_MS);
  }, [forceCollapsed]);

  const onMouseLeave = useCallback(() => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  /** Drop the hover overlay (e.g. after navigating) without touching the lock. */
  const collapseHover = useCallback(() => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
  }, []);

  useEffect(() => {
    window.addEventListener('blur', collapseHover);
    return () => window.removeEventListener('blur', collapseHover);
  }, [collapseHover]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handle = () => {
      setForceCollapsed(mq.matches);
      if (mq.matches) setHovered(false);
    };
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  return useMemo(
    () => ({
      locked,
      expanded,
      hovered,
      toggleLock,
      onMouseEnter,
      onMouseLeave,
      collapseHover,
      forceCollapsed,
      setPeek,
    }),
    [locked, expanded, hovered, toggleLock, onMouseEnter, onMouseLeave, collapseHover, forceCollapsed]
  );
}

export default useSidebarState;
